import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import InternalLinks from '../components/InternalLinks';
import { 
  Shield, 
  Search, 
  FileText, 
  AlertTriangle, 
  Clock, 
  CheckCircle,
  ArrowRight,
  Eye,
  Lock,
  Users,
  TrendingUp,
  Target,
  BadgeCheck
} from 'lucide-react';

export default function MarchesPublicsSecuritePage() {
  return (
    <>
      <SEO 
        title="Marchés Publics Sécurité : Appels d'Offres Gardiennage et Surveillance"
        description="Comment trouver des marchés publics de sécurité et gardiennage. Guide complet : appels d'offres surveillance, analyse DCE sécurité, qualifications requises. Conseils pour entreprises de sécurité privée."
        path="/marches-publics-securite"
      />
      <div className="min-h-screen bg-white">
        <Header />
        
        <Breadcrumb 
          items={[
            { label: 'Marchés publics sécurité' }
          ]} 
        />

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-16 text-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 text-blue-300 rounded-full text-sm font-medium mb-6">
                <Shield className="w-4 h-4" />
                Secteur Sécurité & Gardiennage
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Marchés Publics Sécurité : Trouver des Appels d'Offres en Gardiennage et Surveillance
              </h1>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
                Le secteur de la sécurité privée est un marché porteur dans la commande publique. 
                Découvrez comment identifier les opportunités et répondre efficacement aux appels d'offres 
                de gardiennage, surveillance et sûreté.
              </p>
              <Link to="/">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                  <Search className="w-5 h-5 mr-2" />
                  Trouver des marchés sécurité
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {[
                { value: '3 Md€', label: 'marché sécurité privée/an', icon: TrendingUp },
                { value: '180 000', label: 'agents de sécurité en France', icon: Users },
                { value: 'CNAPS', label: 'autorisation obligatoire', icon: BadgeCheck },
                { value: '+15%', label: 'croissance annuelle', icon: Target }
              ].map((stat, i) => (
                <div key={`item-${i}`} className="bg-white rounded-xl p-6 text-center shadow-sm">
                  <stat.icon className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                  <div className="text-3xl font-bold text-slate-900">{stat.value}</div>
                  <div className="text-sm text-slate-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            
            {/* Section 1 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Shield className="w-8 h-8 text-blue-600" />
                Quels types de marchés publics en sécurité ?
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600">
                <p>
                  Les <strong>marchés publics de sécurité</strong> couvrent un large spectre de prestations, 
                  allant du gardiennage classique à la sûreté aéroportuaire. Voici les principales catégories :
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4 mt-6">
                {[
                  { title: 'Gardiennage et surveillance', desc: 'Rondes, contrôle d\'accès, surveillance de sites publics', icon: Eye },
                  { title: 'Sécurité événementielle', desc: 'Manifestations culturelles, sportives, cérémonies officielles', icon: Users },
                  { title: 'Télésurveillance', desc: 'Vidéoprotection, levée de doute, intervention sur alarme', icon: Lock },
                  { title: 'Protection rapprochée', desc: 'Protection de personnalités, escorte de fonds', icon: Shield }
                ].map((item, i) => (
                  <div key={`item-${i}`} className="bg-blue-50 rounded-xl p-5 border border-blue-100">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-5 h-5 text-blue-700" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{item.title}</h3>
                        <p className="text-sm text-slate-600 mt-1">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mt-6 rounded-r-lg">
                <p className="text-blue-800">
                  <strong>Attention :</strong> Depuis la loi du 12 juillet 1983, l'exercice d'activités de sécurité privée 
                  est soumis à autorisation du CNAPS (Conseil National des Activités Privées de Sécurité). 
                  Cette autorisation est un prérequis obligatoire pour répondre aux marchés publics de sécurité.
                </p>
              </div>
            </div>

            {/* Section 2 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Search className="w-8 h-8 text-blue-600" />
                Où trouver les appels d'offres sécurité ?
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600 mb-6">
                <p>
                  Les marchés de sécurité sont publiés sur les mêmes plateformes que les autres marchés publics, 
                  mais certains acheteurs spécifiques méritent une attention particulière :
                </p>
              </div>

              <div className="space-y-4">
                {[
                  { 
                    name: 'Collectivités territoriales', 
                    desc: 'Mairies, conseils départementaux et régionaux pour la surveillance de bâtiments publics',
                    examples: 'Écoles, musées, bibliothèques, piscines'
                  },
                  { 
                    name: 'Établissements de santé', 
                    desc: 'Hôpitaux, EHPAD, centres hospitaliers universitaires',
                    examples: 'Accueil, rondes, gestion des accès'
                  },
                  { 
                    name: 'Bailleurs sociaux', 
                    desc: 'Offices HLM et organismes de logement social',
                    examples: 'Surveillance de résidences, médiation'
                  },
                  { 
                    name: 'Établissements culturels', 
                    desc: 'Musées nationaux, théâtres, salles de spectacle',
                    examples: 'Sécurité événementielle, gardiennage'
                  },
                  { 
                    name: 'Transports publics', 
                    desc: 'SNCF, RATP, réseaux de transport urbain',
                    examples: 'Sûreté ferroviaire, contrôle d\'accès'
                  }
                ].map((source, i) => (
                  <div key={`item-${i}`} className="flex items-start gap-4 p-4 bg-white border border-slate-200 rounded-xl hover:border-blue-300 transition-colors">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-slate-900">{source.name}</h3>
                      <p className="text-slate-600 text-sm">{source.desc}</p>
                      <span className="text-xs text-slate-400 italic">{source.examples}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-slate-900 text-white rounded-2xl p-6 mt-8">
                <h3 className="text-xl font-bold mb-3">Centralisez votre veille sécurité</h3>
                <p className="text-slate-300 mb-4">
                  Avec notre <Link to="/veille-marches-publics" className="underline font-medium text-blue-300">veille marchés publics</Link>, 
                  recevez automatiquement les appels d'offres correspondant à vos activités de sécurité 
                  sans avoir à consulter chaque plateforme.
                </p>
                <Link to="/">
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    Configurer ma veille sécurité
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Section 3 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <FileText className="w-8 h-8 text-blue-600" />
                Comment analyser un DCE de sécurité ?
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600 mb-6">
                <p>
                  Les DCE de marchés de sécurité comportent des spécificités à bien identifier. 
                  Voici les points clés à vérifier :
                </p>
              </div>

              <div className="space-y-4">
                {[
                  { step: '1', title: 'Qualifications requises', desc: 'Autorisation CNAPS, certifications ISO, labels spécifiques (APSAD...)' },
                  { step: '2', title: 'Volume horaire', desc: 'Nombre d\'heures de vacation, planning type, astreintes éventuelles' },
                  { step: '3', title: 'Profils demandés', desc: 'Agents SSIAP, cynophiles, agents de sûreté aéroportuaire...' },
                  { step: '4', title: 'Équipements exigés', desc: 'Véhicules, tenues, moyens de communication, PTI...' },
                  { step: '5', title: 'Clause sociale', desc: 'Reprise du personnel en place (article L.1224-1 du Code du travail)' }
                ].map((item, i) => (
                  <div key={`item-${i}`} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                      {item.step}
                    </div>
                    <div className="flex-1 bg-slate-50 rounded-xl p-4">
                      <h3 className="font-semibold text-slate-900">{item.title}</h3>
                      <p className="text-slate-600 text-sm mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-slate-600 mt-6">
                Notre <Link to="/analyse-dce" className="text-blue-600 font-medium hover:underline">outil d'analyse DCE</Link> extrait 
                automatiquement ces informations clés pour vous faire gagner du temps.
              </p>
            </div>

            {/* Section 4 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-blue-600" />
                Les erreurs fréquentes des entreprises de sécurité
              </h2>
              
              <div className="space-y-4">
                {[
                  { 
                    error: 'Proposer un prix trop bas',
                    solution: 'Les prix anormalement bas sont un motif de rejet. Respectez la convention collective et les minima sociaux.'
                  },
                  { 
                    error: 'Négliger la clause de reprise du personnel',
                    solution: 'Si elle existe, vous devez intégrer les salariés en place. Vérifiez l\'ancienneté et les conditions.'
                  },
                  { 
                    error: 'Oublier les certifications spécifiques',
                    solution: 'Selon le site, des habilitations peuvent être requises (SSIAP, cynophile, sûreté aéroportuaire...)'
                  },
                  { 
                    error: 'Sous-estimer le mémoire technique',
                    solution: 'Détaillez votre organisation, vos procédures, la formation continue de vos agents.'
                  },
                  { 
                    error: 'Ne pas visiter le site',
                    solution: 'La visite permet d\'identifier les contraintes réelles et d\'ajuster votre offre technique.'
                  }
                ].map((item, i) => (
                  <div key={`item-${i}`} className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-red-600 font-bold text-sm">✗</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{item.error}</h3>
                        <div className="flex items-start gap-2 mt-2">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />
                          <p className="text-slate-600 text-sm">{item.solution}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 5 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Clock className="w-8 h-8 text-blue-600" />
                Optimiser sa veille marchés de sécurité
              </h2>
              
              <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Les clés d'une veille efficace</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3">Mots-clés à surveiller</h4>
                    <ul className="space-y-2 text-slate-600 text-sm">
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Gardiennage</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Surveillance humaine</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Sécurité privée</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Rondes</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Contrôle d'accès</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3">Codes CPV utiles</h4>
                    <ul className="space-y-2 text-slate-600 text-sm">
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> 79710000 - Services de sécurité</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> 79713000 - Services de gardiennage</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> 79714000 - Services de surveillance</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> 79711000 - Surveillance d'alarmes</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-slate-900 to-blue-900 py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Trouvez vos prochains marchés de sécurité
            </h2>
            <p className="text-slate-300 mb-8 max-w-xl mx-auto">
              Recevez gratuitement 3 recommandations de marchés publics 
              correspondant à vos activités de sécurité.
            </p>
            <Link to="/">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                <Shield className="w-5 h-5 mr-2" />
                Commencer ma veille sécurité
              </Button>
            </Link>
          </div>
        </section>

        {/* Internal Links */}
        <InternalLinks currentPage="securite" />

        <Footer />
      </div>
    </>
  );
}
